import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { trackFormSubmit, trackButtonClick } from '../utils/analytics';
import { submitFormToLambda } from '../utils/api';
import { toast } from 'sonner';

const ContactPopup = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Track form submission
    trackFormSubmit('contact_popup', { name: formData.name });

    try {
      // Submit to Lambda API
      await submitFormToLambda('contact_popup', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message
      });

      setIsSuccess(true);
      
      // Reset and close after 3 seconds
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({ name: '', email: '', phone: '', message: '' });
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div 
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md mx-4 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <X size={20} className="text-gray-600" />
        </button>

        <div className="p-8">
          {!isSuccess ? (
            <>
              <h2 className="text-2xl font-bold text-[#0A1F44] mb-2">
                Get in Touch
              </h2>
              <p className="text-gray-600 mb-6">
                Fill out the form and we'll get back to you within 24 hours.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your Name *"
                    className="w-full rounded-lg border-gray-300 focus:border-[#0056D2] focus:ring-[#0056D2]"
                  />
                </div>

                <div>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Email Address *"
                    className="w-full rounded-lg border-gray-300 focus:border-[#0056D2] focus:ring-[#0056D2]"
                  />
                </div>

                <div>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Phone Number *"
                    className="w-full rounded-lg border-gray-300 focus:border-[#0056D2] focus:ring-[#0056D2]"
                  />
                </div>

                <div>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder="Your Message *"
                    className="w-full rounded-lg border-gray-300 focus:border-[#0056D2] focus:ring-[#0056D2]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  onClick={() => trackButtonClick('submit_popup_form', 'contact_popup')}
                  className="w-full bg-[#FF7A00] hover:bg-[#e66d00] text-white font-semibold py-3 rounded-full transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-2"
                >
                  <Send size={18} />
                  <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-[#0A1F44] mb-2">
                Message Sent!
              </h3>
              <p className="text-gray-600">
                Thank you for contacting us. We'll get back to you within 24 hours.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactPopup;